/**
 * Optional due-date badges — plan/30 (company timezone).
 * Empty due = no badge (not overdue).
 * Fulfillment % for list progress bars (ERPNext-style % paid / delivered).
 */
import { d } from "./money.js";
/** YYYY-MM-DD in the given IANA timezone for an instant */
export function calendarDateInZone(instant, timeZone) {
    return instant.toLocaleDateString("en-CA", { timeZone });
}
function toYmd(raw) {
    if (raw instanceof Date) {
        const y = raw.getUTCFullYear();
        const m = String(raw.getUTCMonth() + 1).padStart(2, "0");
        const d = String(raw.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    const s = String(raw).trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}
/** Whole calendar days from `todayYmd` to `dueYmd` (due − today). */
export function calendarDayDelta(todayYmd, dueYmd) {
    const t = Date.UTC(Number(todayYmd.slice(0, 4)), Number(todayYmd.slice(5, 7)) - 1, Number(todayYmd.slice(8, 10)));
    const d = Date.UTC(Number(dueYmd.slice(0, 4)), Number(dueYmd.slice(5, 7)) - 1, Number(dueYmd.slice(8, 10)));
    return Math.round((d - t) / 86_400_000);
}
export function formatDueLabel(days) {
    if (days === 0)
        return "Due today";
    if (days > 0)
        return days === 1 ? "Due in 1 day" : `Due in ${days} days`;
    const n = Math.abs(days);
    return n === 1 ? "Overdue 1 day" : `Overdue ${n} days`;
}
/**
 * @param dueOn - date-only string (YYYY-MM-DD) or Date from DB
 * @param opts.now - evaluation instant
 * @param opts.timeZone - company timezone (default Asia/Manila)
 */
export function dueBadge(dueOn, opts) {
    if (dueOn == null || dueOn === "") {
        return { kind: "none", days: null, label: null };
    }
    const dueYmd = toYmd(dueOn);
    if (!dueYmd)
        return { kind: "none", days: null, label: null };
    const timeZone = opts?.timeZone ?? "Asia/Manila";
    const now = opts?.now ?? new Date();
    const todayYmd = calendarDateInZone(now, timeZone);
    const days = calendarDayDelta(todayYmd, dueYmd);
    if (days === 0) {
        return { kind: "today", days, label: formatDueLabel(days) };
    }
    if (days < 0) {
        return { kind: "overdue", days, label: formatDueLabel(days) };
    }
    return { kind: "upcoming", days, label: formatDueLabel(days) };
}
/** Prefer earliest among payment / deliver / expected */
export function primaryDueBadge(dates, opts) {
    const candidates = [
        dates.paymentDueOn,
        dates.deliverBy,
        dates.expectedOn,
    ].filter((d) => d != null && d !== "");
    let best = null;
    for (const c of candidates) {
        const b = dueBadge(c, opts);
        if (b.kind === "none" || b.days == null)
            continue;
        if (!best || best.days == null || b.days < best.days)
            best = b;
    }
    return best ?? { kind: "none", days: null, label: null };
}
export function matchesDueFilter(badge, filter) {
    if (filter === "all")
        return true;
    if (filter === "none")
        return badge.kind === "none";
    if (filter === "overdue")
        return badge.kind === "overdue";
    if (filter === "this_week") {
        if (badge.kind === "none" || badge.days == null)
            return false;
        return badge.days <= 6;
    }
    return true;
}
export function fulfillmentLabel(parts) {
    const bits = [];
    if (parts.pay === "paid")
        bits.push("Paid");
    else if (parts.pay === "partial")
        bits.push("Partial pay");
    else if (parts.pay === "unpaid")
        bits.push("Unpaid");
    if (parts.deliver === "delivered")
        bits.push("Delivered");
    else if (parts.deliver === "partial")
        bits.push("Partial deliv");
    else if (parts.deliver === "undelivered")
        bits.push("Undelivered");
    if (parts.receive === "received")
        bits.push("Received");
    else if (parts.receive === "partial")
        bits.push("Partial recv");
    else if (parts.receive === "none")
        bits.push("Not received");
    return bits.join(" · ");
}
/**
 * Determinate fulfillment percent for progress bars (0–100).
 * `null` when the denominator is zero / missing (no bar — show em dash).
 * Partials never round to 0% or 100% while still incomplete / not empty.
 */
export function ratioPct(part, whole) {
    if (part == null || part === "" || whole == null || whole === "") {
        return null;
    }
    let p;
    let w;
    try {
        p = d(part);
        w = d(whole);
    }
    catch {
        return null;
    }
    if (!p.isFinite() || !w.isFinite() || w.lte(0))
        return null;
    if (p.lte(0))
        return 0;
    if (p.gte(w))
        return 100;
    const rounded = Math.round(Number(p.div(w).mul(100).toString()));
    return Math.min(99, Math.max(1, rounded));
}
export function isOrderDone(parts) {
    if (parts.lifecycle === "void" || parts.lifecycle === "draft")
        return false;
    if (parts.pay !== "paid")
        return false;
    if (parts.deliver != null)
        return parts.deliver === "delivered";
    if (parts.receive != null)
        return parts.receive === "received";
    return false;
}
export function matchesLifecycleFilter(lifecycle, filter, axes) {
    if (filter === "all")
        return true;
    if (filter === "done") {
        return isOrderDone({ lifecycle, ...axes });
    }
    return lifecycle === filter;
}
export function matchesPayAxisFilter(pay, filter) {
    if (filter === "all")
        return true;
    return pay === filter;
}
export function matchesDeliverAxisFilter(deliver, filter) {
    if (filter === "all")
        return true;
    return deliver === filter;
}
export function matchesReceiveAxisFilter(receive, filter) {
    if (filter === "all")
        return true;
    return receive === filter;
}
